<template>
    <AmModal
        :open="open"
        size="medium"
        eyebrow="АККАУНТ"
        title="Настройки"
        body-flush
        @close="$emit('close')"
    >
            <div class="bento-body">
                <nav class="bento-nav" role="tablist">
                    <button
                        v-for="item in navItems"
                        :key="item.id"
                        type="button"
                        role="tab"
                        class="bento-nav-item"
                        :class="{ 'bento-nav-item--active': activeSection === item.id }"
                        @click="activeSection = item.id"
                    >
                        {{ item.label }}
                    </button>
                </nav>

                <div class="bento-main">
                    <!-- Профиль -->
                    <section v-if="activeSection === 'profile'" class="section">
                        <div class="bento-card bento-card--banner profile-hero">
                            <div class="bento-avatar">{{ initials }}</div>
                            <div class="profile-hero-text">
                                <div class="profile-name">{{ displayName }}</div>
                                <div class="bento-muted bento-small">{{ userInfo.email || '—' }}</div>
                            </div>
                        </div>

                        <form class="bento-card profile-form" @submit.prevent="saveProfile">
                            <div class="bento-grid-2">
                                <div class="bento-field">
                                    <label class="bento-label">Имя</label>
                                    <input v-model="profileForm.name" type="text" class="bento-input" />
                                </div>
                                <div class="bento-field">
                                    <label class="bento-label">Фамилия</label>
                                    <input v-model="profileForm.user_lastname" type="text" class="bento-input" />
                                </div>
                            </div>
                            <div class="bento-field">
                                <label class="bento-label">Компания</label>
                                <input v-model="profileForm.company" type="text" class="bento-input" />
                            </div>
                            <div class="bento-field">
                                <label class="bento-label">Должность</label>
                                <input v-model="profileForm.position" type="text" class="bento-input" />
                            </div>
                            <div class="bento-field">
                                <label class="bento-label">Email</label>
                                <input :value="userInfo.email" type="email" class="bento-input" disabled />
                            </div>
                            <p v-if="profileError" class="form-error">{{ profileError }}</p>
                            <p v-if="profileSaved" class="form-success">Сохранено</p>
                            <AmButton type="submit" variant="dark" :disabled="profileSaving">
                                {{ profileSaving ? 'Сохранение…' : 'Сохранить' }}
                            </AmButton>
                        </form>
                    </section>

                    <!-- Подписка -->
                    <section v-else-if="activeSection === 'subscription'" class="section">
                        <ChatSkeleton v-if="planLoading" variant="card" :count="3" />
                        <template v-else>
                            <div class="bento-card bento-card--banner plan-banner">
                                <div class="plan-big-name">{{ planData?.plan?.name || 'Free' }}</div>
                                <p v-if="planData?.expires_at" class="bento-small" style="margin-top: 10px">
                                    Действует до {{ formatDate(planData.expires_at) }}
                                </p>
                            </div>
                            <div class="bento-grid-2">
                                <div class="bento-card bento-stat">
                                    <div class="bento-stat-value">{{ planData?.plan?.requests_per_day ?? '—' }}</div>
                                    <div class="bento-stat-label">запросов / день</div>
                                </div>
                                <div class="bento-card bento-stat">
                                    <div class="bento-stat-value">{{ formatNum(planData?.plan?.tokens_per_month) }}</div>
                                    <div class="bento-stat-label">токенов / месяц</div>
                                </div>
                            </div>
                            <div v-if="addonSources.length" class="bento-card">
                                <div class="bento-label" style="margin-bottom: 10px">Доп. источники</div>
                                <ul class="addon-list">
                                    <li v-for="src in addonSources" :key="src.code">
                                        {{ src.name }}
                                        <span v-if="src.expires_at" class="bento-muted bento-small">
                                            · до {{ formatDate(src.expires_at) }}
                                        </span>
                                    </li>
                                </ul>
                            </div>
                            <AmButton variant="primary" block @click="$emit('upgrade')">
                                Выбрать тариф
                            </AmButton>
                        </template>
                    </section>

                    <!-- Регионы -->
                    <section v-else-if="activeSection === 'regions'" class="section">
                        <div v-if="regionsLoading" class="bento-muted">Загружаем…</div>
                        <template v-else-if="!regionsPlanEnabled">
                            <div class="bento-card">
                                <p class="bento-muted">
                                    На текущем тарифе выбор регионов недоступен.
                                    Обновите тариф, чтобы настроить регионы.
                                </p>
                                <AmButton variant="primary" block style="margin-top: 12px" @click="$emit('upgrade')">
                                    Выбрать тариф
                                </AmButton>
                            </div>
                        </template>
                        <template v-else>
                            <div class="bento-card">
                                <div class="bento-label" style="margin-bottom: 8px">Текущие регионы</div>
                                <p v-if="!regionDraft.length" class="bento-muted bento-small">Не выбраны</p>
                                <div v-else class="region-chips">
                                    <span v-for="code in regionDraft" :key="code" class="region-chip">
                                        {{ regionLabel(code) }}
                                    </span>
                                </div>
                            </div>

                            <div class="bento-card" style="margin-top: 12px">
                                <div class="bento-label" style="margin-bottom: 8px">Лимиты смены</div>
                                <ul class="quota-list">
                                    <li>
                                        Бесплатно сейчас:
                                        <strong>{{ regionsQuota.can_change_free ? 'да' : 'нет' }}</strong>
                                    </li>
                                    <li v-if="regionsQuota.changes_limit_per_month > 0">
                                        Использовано в этом месяце:
                                        {{ regionsQuota.changes_used_this_month }} /
                                        {{ regionsQuota.changes_limit_per_month }}
                                    </li>
                                    <li v-if="regionsQuota.next_free_at && !regionsQuota.can_change_free">
                                        Следующая бесплатная смена:
                                        {{ formatDate(regionsQuota.next_free_at) }}
                                    </li>
                                    <li v-if="regionsQuota.surcharge_cents > 0">
                                        Доплата сверх лимита:
                                        {{ Math.round(regionsQuota.surcharge_cents / 100).toLocaleString('ru-RU') }} ₽
                                    </li>
                                </ul>
                            </div>

                            <div v-if="regionsEditing" class="bento-card" style="margin-top: 12px">
                                <div class="bento-label" style="margin-bottom: 10px">Новый набор регионов</div>
                                <AmMultiSelect
                                    v-model="regionDraft"
                                    :options="regionOptions"
                                    :limit="regionsLimit"
                                    placeholder="Выберите регионы…"
                                />
                                <p v-if="regionsError" class="form-error" style="margin-top: 10px">{{ regionsError }}</p>
                                <p v-if="regionsPayHint" class="form-warn" style="margin-top: 10px">{{ regionsPayHint }}</p>
                                <div class="region-actions">
                                    <AmButton variant="ghost" :disabled="regionsSaving" @click="cancelRegionsEdit">
                                        Отмена
                                    </AmButton>
                                    <AmButton variant="primary" :disabled="regionsSaving || !regionDraft.length" @click="saveRegions">
                                        {{ regionsSaving ? 'Сохраняем…' : saveRegionsLabel }}
                                    </AmButton>
                                </div>
                            </div>
                            <AmButton
                                v-else
                                variant="dark"
                                block
                                style="margin-top: 12px"
                                @click="startRegionsEdit"
                            >
                                Изменить регионы
                            </AmButton>
                        </template>
                    </section>

                    <!-- Источники -->
                    <section v-else-if="activeSection === 'sources'" class="section">
                        <div v-if="sourcesLoading" class="bento-muted">Загружаем…</div>
                        <template v-else-if="!sourcesPlanEnabled">
                            <div class="bento-card">
                                <p class="bento-muted">
                                    На текущем тарифе выбор источников недоступен.
                                    Обновите тариф, чтобы настроить источники.
                                </p>
                                <AmButton variant="primary" block style="margin-top: 12px" @click="$emit('upgrade')">
                                    Выбрать тариф
                                </AmButton>
                            </div>
                        </template>
                        <template v-else>
                            <div v-if="includedSources.length" class="bento-card">
                                <div class="bento-label" style="margin-bottom: 8px">Включены в тариф</div>
                                <div class="region-chips">
                                    <span v-for="src in includedSources" :key="src.code" class="region-chip">
                                        {{ src.name || src.code }}
                                    </span>
                                </div>
                            </div>

                            <div class="bento-card" style="margin-top: 12px">
                                <div class="bento-label" style="margin-bottom: 8px">Выбранные источники</div>
                                <p v-if="!sourceDraft.length" class="bento-muted bento-small">Не выбраны</p>
                                <div v-else class="region-chips">
                                    <span v-for="code in sourceDraft" :key="code" class="region-chip">
                                        {{ sourceLabel(code) }}
                                    </span>
                                </div>
                            </div>

                            <div class="bento-card" style="margin-top: 12px">
                                <div class="bento-label" style="margin-bottom: 8px">Лимиты смены</div>
                                <ul class="quota-list">
                                    <li>
                                        Бесплатно сейчас:
                                        <strong>{{ sourcesQuota.can_change_free ? 'да' : 'нет' }}</strong>
                                    </li>
                                    <li v-if="sourcesQuota.changes_limit_per_month > 0">
                                        Использовано в этом месяце:
                                        {{ sourcesQuota.changes_used_this_month }} /
                                        {{ sourcesQuota.changes_limit_per_month }}
                                    </li>
                                    <li v-if="sourcesQuota.next_free_at && !sourcesQuota.can_change_free">
                                        Следующая бесплатная смена:
                                        {{ formatDate(sourcesQuota.next_free_at) }}
                                    </li>
                                    <li v-if="sourcesQuota.surcharge_cents > 0">
                                        Доплата сверх лимита:
                                        {{ Math.round(sourcesQuota.surcharge_cents / 100).toLocaleString('ru-RU') }} ₽
                                    </li>
                                </ul>
                            </div>

                            <div v-if="sourcesEditing" class="bento-card" style="margin-top: 12px">
                                <div class="bento-label" style="margin-bottom: 10px">Новый набор источников</div>
                                <AmMultiSelect
                                    v-model="sourceDraft"
                                    :options="sourceOptions"
                                    :limit="sourcesLimit"
                                    placeholder="Выберите источники…"
                                />
                                <p v-if="sourcesError" class="form-error" style="margin-top: 10px">{{ sourcesError }}</p>
                                <p v-if="sourcesPayHint" class="form-warn" style="margin-top: 10px">{{ sourcesPayHint }}</p>
                                <div class="region-actions">
                                    <AmButton variant="ghost" :disabled="sourcesSaving" @click="cancelSourcesEdit">
                                        Отмена
                                    </AmButton>
                                    <AmButton variant="primary" :disabled="sourcesSaving || !sourceDraft.length" @click="saveSources">
                                        {{ sourcesSaving ? 'Сохраняем…' : saveSourcesLabel }}
                                    </AmButton>
                                </div>
                            </div>
                            <AmButton
                                v-else
                                variant="dark"
                                block
                                style="margin-top: 12px"
                                @click="startSourcesEdit"
                            >
                                Изменить источники
                            </AmButton>
                        </template>
                    </section>

                    <!-- Использование -->
                    <section v-else-if="activeSection === 'usage'" class="section">
                        <div class="bento-grid-2">
                            <div class="bento-card">
                                <div class="bento-stat-value">{{ requestsUsed }}</div>
                                <div class="bento-stat-label">запросов использовано за сегодня</div>
                                <div class="bento-progress">
                                    <div class="bento-progress-fill" :style="{ width: requestsPercent + '%' }" />
                                </div>
                                <div class="bento-muted bento-small" style="margin-top: 8px">из {{ requestsTotal }} в день</div>
                            </div>
                            <div class="bento-card">
                                <div class="bento-stat-value">{{ formatNum(tokensUsed) }}</div>
                                <div class="bento-stat-label">токенов использовано за месяц</div>
                                <div class="bento-progress">
                                    <div class="bento-progress-fill" :style="{ width: tokensPercent + '%' }" />
                                </div>
                                <div class="bento-muted bento-small" style="margin-top: 8px">из {{ formatNum(tokensTotal) }} в месяц</div>
                            </div>
                        </div>
                    </section>

                    <!-- Оплаты -->
                    <section v-else-if="activeSection === 'payments'" class="section">
                        <div class="quick-nav">
                            <button type="button" class="bento-btn bento-btn--ghost" @click="activeSection = 'subscription'">
                                Подписка →
                            </button>
                            <button type="button" class="bento-btn bento-btn--ghost" @click="$emit('upgrade')">
                                Тарифы →
                            </button>
                            <button type="button" class="bento-btn bento-btn--ghost" @click="$emit('open-tickets', 'billing')">
                                Проблема с оплатой?
                            </button>
                        </div>

                        <div class="bento-card payments-card">
                            <div class="payments-head">
                                <span class="payments-title">История оплат</span>
                                <button
                                    v-if="orders.length > previewLimit"
                                    type="button"
                                    class="bento-btn bento-btn--ghost bento-btn--sm"
                                    @click="showAllOrders = !showAllOrders"
                                >
                                    {{ showAllOrders ? 'Свернуть' : `Все (${orders.length})` }}
                                </button>
                            </div>
                            <ChatSkeleton v-if="ordersLoading" variant="table" :count="5" />
                            <div v-else-if="displayedOrders.length === 0" class="state-empty">Покупок пока нет</div>
                            <table v-else class="payments-table">
                                <thead>
                                    <tr>
                                        <th>Название</th>
                                        <th>Сумма</th>
                                        <th>Статус</th>
                                        <th>Дата</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="order in displayedOrders" :key="order.id">
                                        <td>
                                            <div class="order-title-cell">{{ order.title }}</div>
                                            <div class="order-type-cell">{{ formatProductType(order.product_type) }}</div>
                                        </td>
                                        <td>{{ formatOrderPrice(order.amount_cents) }}</td>
                                        <td>
                                            <span class="bento-pill" :class="order.status === 'paid' ? 'bento-pill--paid' : 'bento-pill--pending'">
                                                {{ formatOrderStatus(order.status) }}
                                            </span>
                                        </td>
                                        <td class="date-cell">{{ formatDate(order.created_at) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            </div>
    </AmModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import AmButton from '@/components/ui/AmButton.vue'
import AmModal from '@/components/ui/AmModal.vue'
import AmMultiSelect from '@/components/ui/AmMultiSelect.vue'
import ChatSkeleton from '@/components/chat/ChatSkeleton.vue'
import {
    getChatPlan,
    getChatRegionsCurrent,
    getChatRegionsConfigPreview,
    changeChatRegions,
    getChatSourcesCurrent,
    changeChatSources,
} from '@/utils/chatApi'
import {
    fetchPaymentOrders,
    formatOrderPrice,
    formatOrderStatus,
    formatProductType,
} from '@/utils/paymentsApi'
import { updateUser } from '@/utils/requests'

const props = defineProps({
    open: { type: Boolean, default: false },
    initialSection: { type: String, default: 'profile' },
    userInfo: { type: Object, default: () => ({}) },
    tokensUsed: { type: Number, default: 0 },
    tokensTotal: { type: Number, default: 0 },
    requestsUsed: { type: Number, default: 0 },
    requestsTotal: { type: Number, default: 0 },
})

const emit = defineEmits(['close', 'upgrade', 'open-tickets', 'profile-saved'])

const store = useStore()

const navItems = [
    { id: 'profile', label: 'Профиль' },
    { id: 'subscription', label: 'Подписка' },
    { id: 'regions', label: 'Регионы' },
    { id: 'sources', label: 'Источники' },
    { id: 'usage', label: 'Использование' },
    { id: 'payments', label: 'Оплаты' },
]

const activeSection = ref('profile')
const showAllOrders = ref(false)
const previewLimit = 6

const profileForm = ref({ name: '', user_lastname: '', company: '', position: '' })
const profileSaving = ref(false)
const profileError = ref('')
const profileSaved = ref(false)

const planLoading = ref(false)
const planData = ref(null)
const ordersLoading = ref(false)
const orders = ref([])

const regionsLoading = ref(false)
const regionsEditing = ref(false)
const regionsSaving = ref(false)
const regionsError = ref('')
const regionsPayHint = ref('')
const regionsCurrent = ref(null)
const regionDraft = ref([])
const regionCatalog = ref([])
const regionsQuota = ref({
    can_change_free: false,
    changes_used_this_month: 0,
    changes_limit_per_month: 0,
    next_free_at: null,
    surcharge_cents: 0,
})

const regionsPlanEnabled = computed(() =>
    Boolean(regionsCurrent.value?.plan?.region_selection_enabled),
)
const regionsLimit = computed(() =>
    Number(regionsCurrent.value?.plan?.regions_limit || 0),
)
const regionOptions = computed(() =>
    regionCatalog.value.map(r => ({ value: r.code, label: r.name })),
)
const saveRegionsLabel = computed(() => {
    if (regionsQuota.value.can_change_free) return 'Сохранить'
    if (regionsQuota.value.surcharge_cents > 0) {
        const rub = Math.round(regionsQuota.value.surcharge_cents / 100)
        return `Оплатить ${rub.toLocaleString('ru-RU')} ₽ и сменить`
    }
    return 'Сохранить'
})

const sourcesLoading = ref(false)
const sourcesEditing = ref(false)
const sourcesSaving = ref(false)
const sourcesError = ref('')
const sourcesPayHint = ref('')
const sourcesCurrent = ref(null)
const sourceDraft = ref([])
const sourceCatalog = ref([])
const includedSources = ref([])
const sourcesQuota = ref({
    can_change_free: false,
    changes_used_this_month: 0,
    changes_limit_per_month: 0,
    next_free_at: null,
    surcharge_cents: 0,
})

const sourcesPlanEnabled = computed(() =>
    Boolean(sourcesCurrent.value?.plan?.source_selection_enabled),
)
const sourcesLimit = computed(() =>
    Number(sourcesCurrent.value?.plan?.sources_limit || 0),
)
const sourceOptions = computed(() =>
    sourceCatalog.value.map(s => ({ value: s.code, label: s.name })),
)
const saveSourcesLabel = computed(() => {
    if (sourcesQuota.value.can_change_free) return 'Сохранить'
    if (sourcesQuota.value.surcharge_cents > 0) {
        const rub = Math.round(sourcesQuota.value.surcharge_cents / 100)
        return `Оплатить ${rub.toLocaleString('ru-RU')} ₽ и сменить`
    }
    return 'Сохранить'
})

const initials = computed(() => {
    const source = displayName.value || ''
    const parts = source.split(' ').filter(Boolean)
    if (parts.length === 0) return 'AM'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
})

const displayName = computed(() => {
    const u = props.userInfo
    if (u?.name) return [u.name, u.user_lastname].filter(Boolean).join(' ')
    return 'Аналитик'
})

const addonSources = computed(() => planData.value?.addon_sources || [])

const requestsPercent = computed(() => {
    if (!props.requestsTotal) return 0
    return Math.min(100, Math.round((props.requestsUsed / props.requestsTotal) * 100))
})

const tokensPercent = computed(() => {
    if (!props.tokensTotal) return 0
    return Math.min(100, Math.round((props.tokensUsed / props.tokensTotal) * 100))
})

const displayedOrders = computed(() => {
    if (showAllOrders.value) return orders.value
    return orders.value.slice(0, previewLimit)
})

function syncProfileForm() {
    const u = props.userInfo
    profileForm.value = {
        name: u?.name || '',
        user_lastname: u?.user_lastname || '',
        company: u?.company || '',
        position: u?.position || '',
    }
}

async function saveProfile() {
    const userId = props.userInfo?.id
    if (!userId) return
    profileSaving.value = true
    profileError.value = ''
    profileSaved.value = false
    try {
        const response = await updateUser(userId, {
            name: profileForm.value.name,
            user_lastname: profileForm.value.user_lastname,
            company: profileForm.value.company,
            position: profileForm.value.position,
        })
        if (!response.ok) {
            throw new Error('Не удалось сохранить')
        }
        await store.dispatch('main/refreshCurrentUser')
        const updated = store.getters['main/userInfo'] || { ...props.userInfo, ...profileForm.value }
        store.commit('main/setUserInfo', updated)
        emit('profile-saved', updated)
        profileSaved.value = true
        setTimeout(() => { profileSaved.value = false }, 2500)
    } catch (e) {
        profileError.value = e?.message || 'Ошибка сохранения'
    } finally {
        profileSaving.value = false
    }
}

function formatDate(iso) {
    if (!iso) return '—'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatNum(n) {
    if (n == null) return '—'
    return Number(n).toLocaleString('ru-RU')
}

async function loadPlan() {
    planLoading.value = true
    try {
        planData.value = await getChatPlan()
    } catch {
        planData.value = null
    } finally {
        planLoading.value = false
    }
}

async function loadOrders() {
    ordersLoading.value = true
    try {
        const { items } = await fetchPaymentOrders({ per_page: 100 })
        orders.value = items
    } catch {
        orders.value = []
    } finally {
        ordersLoading.value = false
    }
}

async function loadRegions() {
    regionsLoading.value = true
    regionsEditing.value = false
    regionsError.value = ''
    regionsPayHint.value = ''
    try {
        const data = await getChatRegionsCurrent()
        regionsCurrent.value = data
        regionsQuota.value = data?.quota || regionsQuota.value
        regionDraft.value = [...(data?.selection?.regions_global || [])]
        const planCode = data?.plan?.code
        if (planCode && data?.plan?.region_selection_enabled) {
            const preview = await getChatRegionsConfigPreview(planCode)
            regionCatalog.value = preview?.all_regions || []
        } else {
            regionCatalog.value = []
        }
    } catch {
        regionsCurrent.value = null
        regionCatalog.value = []
    } finally {
        regionsLoading.value = false
    }
}

function regionLabel(code) {
    return regionCatalog.value.find(r => r.code === code)?.name || code
}

function startRegionsEdit() {
    regionsEditing.value = true
    regionsError.value = ''
    regionsPayHint.value = ''
    regionDraft.value = [...(regionsCurrent.value?.selection?.regions_global || [])]
    if (!regionsQuota.value.can_change_free && regionsQuota.value.surcharge_cents > 0) {
        const rub = Math.round(regionsQuota.value.surcharge_cents / 100)
        regionsPayHint.value = `Бесплатная смена недоступна. Будет создан платёж на ${rub.toLocaleString('ru-RU')} ₽.`
    } else if (!regionsQuota.value.can_change_free) {
        regionsPayHint.value = 'Бесплатная смена недоступна, доплата не настроена в тарифе.'
    }
}

function cancelRegionsEdit() {
    regionsEditing.value = false
    regionsError.value = ''
    regionsPayHint.value = ''
    regionDraft.value = [...(regionsCurrent.value?.selection?.regions_global || [])]
}

async function saveRegions() {
    regionsSaving.value = true
    regionsError.value = ''
    try {
        const overrides = regionsCurrent.value?.selection?.source_overrides || {}
        const result = await changeChatRegions({
            regions_global: [...regionDraft.value],
            source_overrides: overrides,
        })
        if (result?.applied) {
            regionsEditing.value = false
            await loadRegions()
        } else if (!result?.payment_url) {
            regionsError.value = result?.detail || 'Не удалось сменить регионы'
        }
        // при payment_url changeChatRegions сам делает redirect
    } catch (e) {
        regionsError.value = e?.message || 'Ошибка смены регионов'
    } finally {
        regionsSaving.value = false
    }
}

async function loadSources() {
    sourcesLoading.value = true
    sourcesEditing.value = false
    sourcesError.value = ''
    sourcesPayHint.value = ''
    try {
        const data = await getChatSourcesCurrent()
        sourcesCurrent.value = data
        sourcesQuota.value = data?.quota || sourcesQuota.value
        sourceDraft.value = [...(data?.selection?.sources_global || [])]
        sourceCatalog.value = data?.available_sources || []
        includedSources.value = data?.included_sources || []
    } catch {
        sourcesCurrent.value = null
        sourceCatalog.value = []
        includedSources.value = []
    } finally {
        sourcesLoading.value = false
    }
}

function sourceLabel(code) {
    return sourceCatalog.value.find(s => s.code === code)?.name || code
}

function startSourcesEdit() {
    sourcesEditing.value = true
    sourcesError.value = ''
    sourcesPayHint.value = ''
    sourceDraft.value = [...(sourcesCurrent.value?.selection?.sources_global || [])]
    if (!sourcesQuota.value.can_change_free && sourcesQuota.value.surcharge_cents > 0) {
        const rub = Math.round(sourcesQuota.value.surcharge_cents / 100)
        sourcesPayHint.value = `Бесплатная смена недоступна. Будет создан платёж на ${rub.toLocaleString('ru-RU')} ₽.`
    } else if (!sourcesQuota.value.can_change_free) {
        sourcesPayHint.value = 'Бесплатная смена недоступна, доплата не настроена в тарифе.'
    }
}

function cancelSourcesEdit() {
    sourcesEditing.value = false
    sourcesError.value = ''
    sourcesPayHint.value = ''
    sourceDraft.value = [...(sourcesCurrent.value?.selection?.sources_global || [])]
}

async function saveSources() {
    sourcesSaving.value = true
    sourcesError.value = ''
    try {
        const result = await changeChatSources({
            sources_global: [...sourceDraft.value],
        })
        if (result?.applied) {
            sourcesEditing.value = false
            await loadSources()
        } else if (!result?.payment_url) {
            sourcesError.value = result?.detail || 'Не удалось сменить источники'
        }
    } catch (e) {
        sourcesError.value = e?.message || 'Ошибка смены источников'
    } finally {
        sourcesSaving.value = false
    }
}

watch(
    () => props.open,
    (isOpen) => {
        if (!isOpen) return
        activeSection.value = props.initialSection || 'profile'
        showAllOrders.value = props.initialSection === 'payments'
        syncProfileForm()
        loadPlan()
        loadOrders()
        loadRegions()
        loadSources()
    },
)

watch(() => props.userInfo, syncProfileForm, { deep: true })
</script>

<style scoped>
.section {
    max-width: 100%;
}

.profile-hero {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 16px;
}

.profile-hero-text {
    flex: 1;
}

.profile-name {
    font-size: 16px;
    font-weight: 600;
    color: #23282C;
}

.profile-form {
    margin-top: 0;
}

.plan-big-name {
    font-size: 20px;
    font-weight: 600;
}

.addon-list {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 1.6;
}

.region-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.region-chip {
    display: inline-flex;
    padding: 4px 10px;
    border-radius: 999px;
    background: #f3f4f5;
    border: 1px solid #e7e7e7;
    font-size: 12.5px;
    color: #23282C;
}
.quota-list {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 1.7;
    color: #23282C;
}
.region-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
}
.form-warn {
    color: #8A5A00;
    background: #FFF5E8;
    padding: 8px 10px;
    border-radius: 8px;
    font-size: 12.5px;
    margin: 0;
}

.quick-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
}

.payments-card {
    padding: 0;
    overflow: hidden;
    background: white;
}

.payments-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid #e0e0e0;
}

.payments-title {
    font-size: 13px;
    font-weight: 600;
    color: #23282C;
}

.payments-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
}

.payments-table th {
    text-align: left;
    padding: 10px 18px;
    color: #888888;
    font-weight: 500;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.payments-table td {
    padding: 12px 18px;
    border-top: 1px solid #e7e7e7;
    color: #23282C;
}

.order-title-cell {
    font-weight: 500;
}

.order-type-cell {
    font-size: 11px;
    color: #888888;
    margin-top: 2px;
}

.date-cell {
    color: #888888;
    white-space: nowrap;
}

.state-empty {
    padding: 24px;
    text-align: center;
    font-size: 13px;
    color: #888888;
}

.form-error {
    color: #E30613;
    font-size: 12px;
    margin: 0 0 8px;
}

.form-success {
    color: #5f5f5f;
    font-size: 12px;
    margin: 0 0 8px;
}

</style>
